import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import Menu from "../../panel/Menu";
import moment from "moment";

import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Pagination,
  Chip,
  CircularProgress
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";

function Checkouts() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000
  });

  const [bookings, setBookings] = useState([]);
  const [propertyId, setPropertyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  // Pagination
  const rowsPerPage = 10;
  const [page, setPage] = useState(1);

  // ================= STEP 1: Get User Property ID =================
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const roleRef = doc(db, "authroles", user.uid);
          const snap = await getDoc(roleRef);
          if (snap.exists()) {
            setPropertyId(snap.data().propertyId);
          }
        } catch (error) {
          console.error("Error fetching role:", error);
        }
      }
    });
    return () => unsub();
  }, []);

  // ================= STEP 2: Fetch Data with propertyId =================
  useEffect(() => {
    if (!propertyId) return;

    setLoading(true);
    try {
      // Query ordered by extra4 in descending order
      const q = query(
        collection(db, "reservations"),
        where("propertyId", "==", propertyId),
        where("status", "==", "Checkout"),
        orderBy("invoiceno", "desc"),
        limit(500)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        setBookings(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            data: doc.data()
          }))
        );
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      Toast.fire({ icon: "error", title: error.message });
      setLoading(false);
    }
  }, [propertyId]);

  // ================= STEP 3: Optimized Filtering =================
  const filteredBookings = useMemo(() => {
    return bookings.filter((item) => {
      const custName = item.data.customers?.custname?.toUpperCase() || "";
      const mobile = item.data.customers?.mobile || "";
      const invoice = item.data.invoiceno?.toString() || "";
      const search = searchText.toUpperCase();

      return (
        custName.includes(search) ||
        mobile.includes(searchText) ||
        invoice.includes(searchText)
      );
    });
  }, [searchText, bookings]);

  const paginatedData = filteredBookings.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ display: "flex", bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Menu />

      <Box className="content-wrapper" component="main" sx={{ flexGrow: 1, p: 2 }}>
        {/* Top Header Bar */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={700} color="#0f172a">
            Checkout History
          </Typography>
          <Chip
            label={`Total: ${filteredBookings.length}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 700, borderRadius: "6px" }}
          />
        </Stack>

        {/* Main Content Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff"
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            {/* Search Input */}
            <Stack direction="row" spacing={2} mb={2}>
              <TextField
                size="small"
                placeholder="Search name, mobile or invoice..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
                sx={{
                  width: 320,
                  bgcolor: "#fff",
                  "& .MuiInputBase-input": { fontSize: "0.85rem", py: 0.8 }
                }}
              />
            </Stack>

            {/* Compact Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #f1f5f9", borderRadius: 1.5 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Sr No</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Customer Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Stay Dates</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Rooms</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Paid</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Remaining</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1, fontSize: "0.75rem" }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "0.8rem" }}>
                          Fetching History...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <TableRow hover key={item.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell sx={{ py: 0.8, fontSize: "0.8rem" }}>
                          {(page - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ py: 0.8 }}>
                          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
                            #{item.data.invoiceno}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.8 }}>
                          <Typography variant="subtitle2" sx={{ textTransform: "capitalize", fontWeight: 700, fontSize: "0.8rem" }}>
                            {item.data.customers?.custname || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" fontSize="0.72rem">
                            Mob: {item.data.customers?.mobile || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.8 }}>
                          <Typography variant="caption" display="block" fontSize="0.72rem" color="text.secondary">
                            IN: {moment(item.data.checkindate).format("DD MMM YYYY")}
                          </Typography>
                          <Typography variant="caption" display="block" fontWeight={600} fontSize="0.72rem" color="text.primary">
                            OUT: {moment(item.data.checkoutdate).format("DD MMM YYYY")}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.8 }}>
                          {item.data.rooms?.map((r, i) => (
                            <Chip
                              key={i}
                              label={r.roomno}
                              size="small"
                              variant="outlined"
                              sx={{
                                mr: 0.5,
                                mb: 0.5,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                height: 20
                              }}
                            />
                          ))}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.8, fontSize: "0.8rem", fontWeight: 600 }}>
                          ₹{item.data.totalamount?.toLocaleString() ?? 0}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.8, color: "success.main", fontWeight: 700, fontSize: "0.8rem" }}>
                          ₹{item.data.advance?.toLocaleString() ?? 0}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            py: 0.8,
                            color: (item.data.remaining ?? 0) > 0 ? "error.main" : "text.secondary",
                            fontWeight: (item.data.remaining ?? 0) > 0 ? 700 : 400,
                            fontSize: "0.8rem"
                          }}
                        >
                          ₹{item.data.remaining?.toLocaleString() ?? 0}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.8 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            href={`/print-invoice/${item.id}`}
                            sx={{ bgcolor: "#eff6ff", p: 0.5 }}
                          >
                            <PrintIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary" fontSize="0.85rem">
                          No checkout records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {filteredBookings.length > rowsPerPage && (
              <Stack alignItems="center" mt={2}>
                <Pagination
                  count={Math.ceil(filteredBookings.length / rowsPerPage)}
                  page={page}
                  onChange={(e, val) => setPage(val)}
                  color="primary"
                  size="small"
                  shape="rounded"
                />
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Checkouts;